import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import moment from 'moment';

class BookingDashboard extends Component {
	constructor(props) {
		super(props);
		this.state = {
			healthMetrics: this.calculateHealthMetrics(),
			activityStats: this.calculateActivityStats(),
			recentBookings: this.getRecentBookings()
		};
	}

	componentDidUpdate(prevProps) {
		if (prevProps.bookingPage !== this.props.bookingPage) {
			this.setState({
				healthMetrics: this.calculateHealthMetrics(),
				activityStats: this.calculateActivityStats(),
				recentBookings: this.getRecentBookings()
			});
		}
	}

	calculateHealthMetrics = () => {
		const { bookingPage } = this.props;
		const issues = [];
		const warnings = [];

		// Check for critical issues
		if (!bookingPage.calendars || bookingPage.calendars.length === 0) {
			issues.push('No calendars configured');
		}

		if (!bookingPage.availability || !bookingPage.availability.enabled) {
			issues.push('Availability rules not configured');
		}

		if (!bookingPage.isActive) {
			warnings.push('Booking system is inactive');
		}

		// Check for inactive calendars
		const inactiveCalendars = bookingPage.calendars ?
			bookingPage.calendars.filter(cal => cal.status !== 'active').length : 0;

		if (inactiveCalendars > 0) {
			warnings.push(`${inactiveCalendars} calendar${inactiveCalendars > 1 ? 's' : ''} inactive`);
		}

		// Check for missing email templates
		if (!bookingPage.emails || (!bookingPage.emails.confirmationEmail && !bookingPage.emails.reminderEmail)) {
			warnings.push('No email templates configured');
		}

		const healthScore = Math.max(0, 100 - (issues.length * 25) - (warnings.length * 10));
		let healthStatus = 'excellent';
		if (healthScore < 50) healthStatus = 'poor';
		else if (healthScore < 75) healthStatus = 'fair';
		else if (healthScore < 90) healthStatus = 'good';

		return {
			score: healthScore,
			status: healthStatus,
			issues,
			warnings,
			totalProblems: issues.length + warnings.length
		};
	};

	calculateActivityStats = () => {
		const { bookingPage } = this.props;
		const meetings = bookingPage.meetings || [];
		const calendars = bookingPage.calendars || [];

		// Calculate stats for last 30 days
		const thirtyDaysAgo = moment().subtract(30, 'days');
		const recentMeetings = meetings.filter(m => moment(m.createdAt).isAfter(thirtyDaysAgo));
		const confirmedMeetings = recentMeetings.filter(m => m.confirmed);
		const pendingMeetings = recentMeetings.filter(m => !m.confirmed);

		// Calculate conversion rate
		const totalBookingAttempts = recentMeetings.length;
		const conversionRate = totalBookingAttempts > 0 ?
			Math.round((confirmedMeetings.length / totalBookingAttempts) * 100) : 0;

		// Find most popular calendar
		const calendarBookings = {};
		confirmedMeetings.forEach(meeting => {
			const calendarId = meeting.calendarId || 'unknown';
			calendarBookings[calendarId] = (calendarBookings[calendarId] || 0) + 1;
		});

		const mostPopularCalendar = Object.keys(calendarBookings).reduce((a, b) =>
			calendarBookings[a] > calendarBookings[b] ? a : b, 'none'
		);

		const popularCalendarName = calendars.find(cal => cal._id === mostPopularCalendar)?.name || 'None';

		return {
			last30Days: {
				totalBookings: recentMeetings.length,
				confirmedBookings: confirmedMeetings.length,
				pendingBookings: pendingMeetings.length,
				conversionRate
			},
			popular: {
				calendar: popularCalendarName
			},
			activeCalendars: calendars.filter(cal => cal.status === 'active').length,
			totalSlots: calendars.reduce((total, cal) => total + (cal.timeSlots?.length || 0), 0)
		};
	};

	getRecentBookings = () => {
		const { bookingPage } = this.props;
		const meetings = bookingPage.meetings || [];

		return meetings
			.sort((a, b) => moment(b.createdAt).unix() - moment(a.createdAt).unix())
			.slice(0, 5)
			.map(meeting => ({
				...meeting,
				timeAgo: moment(meeting.createdAt).fromNow(),
				scheduledDate: moment(meeting.scheduledTime).format('MMM D, YYYY'),
				scheduledTime: moment(meeting.scheduledTime).format('h:mm A')
			}));
	};

	navigateToSection = (section) => {
		const { orgId } = this.props;
		const { bookingPage } = this.props;
		this.props.history.push(`/${orgId}/booking/${bookingPage._id}/${section}`);
	};

	getHealthColor = (status) => {
		switch (status) {
			case 'excellent': return '#10b981';
			case 'good': return '#10b981';
			case 'fair': return '#f59e0b';
			case 'poor': return '#ef4444';
			default: return '#6b7280';
		}
	};

	render() {
		const { healthMetrics, activityStats, recentBookings } = this.state;
		const { bookingPage } = this.props;

		return (
			<div className="sectionContainer">
				<div className="sectionBody">
					<div className='dashboardCard'>
						<div className='dashboardCardHeader'>
							<div className='gray'><h5>Booking Page</h5></div>
							<p className='small'>{bookingPage.published ? <span>Published</span> : <span>Unpublished</span>}</p>
						</div>
						<div>
							<h1>{bookingPage.name}</h1>
							<p>{bookingPage.description}</p>
							<p><strong>Duration:</strong> {bookingPage.duration} minutes</p>
						</div>
						<div className="statsRow">
							<div className="statCard">
									<h3>{activityStats.last30Days.totalBookings}</h3>
								<div className="statContent">
									<p>Total Bookings (30 days)</p>
								</div>
							</div>

							<div className="statCard">
									<h3>{activityStats.last30Days.confirmedBookings}</h3>
								<div className="statContent">
									<p>Confirmed Bookings</p>
								</div>
							</div>

							<div className="statCard">
									<h3>{activityStats.last30Days.pendingBookings}</h3>
								<div className="statContent">
									<p>Pending Bookings</p>
								</div>
							</div>
						</div>
					</div>
					<div className="blocksContainer">

						{/* Stats Overview - 4 Cards */}
					</div>
				</div>

				<style jsx>{`
					.sectionBody {
						padding: 0;
					}

					.blocksContainer {
						display: flex;
						flex-direction: column;
						gap: 30px;
					}

					.dashboardCard {
						position: relative;
						background: white;
						border: 1px solid #e5e7eb;
						border-radius: 12px;
						padding: 24px;
						display: flex;
						flex-direction: column;
					}

					.dashboardCardHeader {
						width: 100%;
						display: flex;
						align-items: center;
						justify-content: space-between;
						margin-bottom: 30px;
					}

					.statsRow {
						margin-top: 24px;
						display: grid;
						grid-template-columns: repeat(4, 1fr);
						gap: 20px;
					}

					.statCard {
						background: white;
						border: 1px solid #e5e7eb;
						border-radius: 12px;
						padding: 24px;
						display: flex;
						align-items: center;
						gap: 16px;
						transition: all 0.2s;
					}

					.statCard:hover {
						box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
						border-color: #d1d5db;
					}

					.statIcon {
						width: 48px;
						height: 48px;
						border-radius: 12px;
						display: flex;
						align-items: center;
						justify-content: center;
						font-size: 24px;
						flex-shrink: 0;
					}

					.statCard h3 {
						font-size: 28px;
						font-weight: 700;
						margin: 0 0 4px 0;
						color: #111827;
					}

					.statContent p {
						font-size: 14px;
						color: #6b7280;
						margin: 0;
					}

					.twoColumnRow {
						display: grid;
						grid-template-columns: 2fr 3fr;
						gap: 30px;
					}

					.sectionHeader {
						display: flex;
						align-items: center;
						justify-content: space-between;
						margin-bottom: 24px;
					}

					.healthOverview {
						display: flex;
						align-items: center;
						gap: 24px;
						margin-bottom: 24px;
					}

					.healthScore {
						width: 80px;
						height: 80px;
						border-radius: 50%;
						display: flex;
						align-items: center;
						justify-content: center;
						flex-shrink: 0;
					}

					.healthScoreInner {
						width: 60px;
						height: 60px;
						background: white;
						border-radius: 50%;
						display: flex;
						align-items: center;
						justify-content: center;
					}

					.healthNumber {
						font-size: 20px;
						font-weight: 700;
						color: #111827;
					}

					.healthContent h4 {
						margin: 0 0 8px 0;
						font-size: 18px;
						font-weight: 600;
					}

					.healthContent p {
						color: #6b7280;
						font-size: 14px;
						margin: 0;
					}

					.issuesList {
						border-top: 1px solid #e5e7eb;
						padding-top: 20px;
					}

					.issueItem {
						display: flex;
						align-items: center;
						gap: 12px;
						padding: 8px 0;
						font-size: 14px;
					}

					.issueItem.critical {
						color: #ef4444;
					}

					.issueItem.warning {
						color: #f59e0b;
					}

					.overviewList {
						display: flex;
						flex-direction: column;
						gap: 24px;
						padding: 10px 0;
					}

					.overviewItem {
						display: flex;
						justify-content: space-between;
						align-items: center;
						padding: 20px 0;
						border-bottom: 1px solid #f3f4f6;
					}

					.overviewItem:last-child {
						border-bottom: none;
					}

					.overviewItem span {
						font-size: 15px;
						color: #6b7280;
						font-weight: 500;
					}

					.overviewItem strong {
						font-weight: 600;
						color: #111827;
						font-size: 17px;
					}

					.statusBadge {
						padding: 8px 14px;
						border-radius: 8px;
						font-size: 12px;
						font-weight: 600;
						text-transform: uppercase;
						letter-spacing: 0.5px;
						white-space: nowrap;
						flex-shrink: 0;
					}

					.statusBadge.active, .statusBadge.confirmed {
						background: #d1fae5;
						color: #065f46;
					}

					.statusBadge.inactive, .statusBadge.pending {
						background: #fef3c7;
						color: #92400e;
					}

					.activityList {
						display: flex;
						flex-direction: column;
						gap: 0;
						border: 1px solid #e5e7eb;
						border-radius: 12px;
						overflow: hidden;
					}

					.activityItem {
						display: flex;
						align-items: flex-start;
						gap: 20px;
						padding: 24px;
						border-bottom: 1px solid #f3f4f6;
						background: #fafafa;
					}

					.activityItem:last-child {
						border-bottom: none;
					}

					.activityDot {
						width: 10px;
						height: 10px;
						border-radius: 50%;
						margin-top: 8px;
						flex-shrink: 0;
					}

					.activityDot.confirmed {
						background: #10b981;
					}

					.activityDot.pending {
						background: #f59e0b;
					}

					.activityContent {
						flex: 1;
						min-width: 0;
					}

					.activityMain {
						display: grid;
						grid-template-columns: 1fr auto;
						gap: 30px;
						align-items: flex-start;
						margin-bottom: 16px;
					}

					.activityInfo {
						min-width: 0;
					}

					.activityInfo h4 {
						margin: 0 0 6px 0;
						font-size: 16px;
						font-weight: 600;
						color: #111827;
						overflow: hidden;
						text-overflow: ellipsis;
						white-space: nowrap;
					}

					.activityInfo p {
						margin: 0;
						font-size: 14px;
						color: #6b7280;
						overflow: hidden;
						text-overflow: ellipsis;
						white-space: nowrap;
					}

					.activityMeta {
						display: flex;
						flex-direction: column;
						align-items: flex-end;
						gap: 10px;
						flex-shrink: 0;
						min-width: 120px;
					}

					.timeAgo {
						font-size: 12px;
						color: #9ca3af;
						white-space: nowrap;
					}

					.activityDate {
						font-size: 13px;
						color: #6b7280;
						text-align: right;
						line-height: 1.3;
					}

					.actionsGrid {
						display: grid;
						grid-template-columns: repeat(2, 1fr);
						gap: 20px;
					}

					.actionButton {
						display: flex;
						align-items: center;
						gap: 16px;
						padding: 20px;
						background: white;
						border: 1px solid #e5e7eb;
						border-radius: 12px;
						cursor: pointer;
						transition: all 0.2s;
						text-align: left;
					}

					.actionButton:hover {
						border-color: #3b82f6;
						box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
					}

					.actionIcon {
						width: 48px;
						height: 48px;
						border-radius: 12px;
						display: flex;
						align-items: center;
						justify-content: center;
						font-size: 20px;
						flex-shrink: 0;
					}

					.actionContent h4 {
						margin: 0 0 4px 0;
						font-size: 16px;
						font-weight: 600;
						color: #111827;
					}

					.actionContent p {
						margin: 0;
						font-size: 14px;
						color: #6b7280;
					}

					@media (max-width: 1200px) {
						.statsRow {
							grid-template-columns: repeat(2, 1fr);
						}
						
						.twoColumnRow {
							grid-template-columns: 1fr;
						}
					}

					@media (max-width: 768px) {
						.statsRow {
							grid-template-columns: 1fr;
						}
						
						.actionsGrid {
							grid-template-columns: 1fr;
						}

						.activityMain {
							grid-template-columns: 1fr;
							gap: 16px;
						}

						.activityMeta {
							align-items: flex-start;
						}
					}
				`}</style>
			</div>
		);
	}
}

export default withRouter(BookingDashboard);
